import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AssessQueryKeys,
  FilesQueryKeys,
  FileUploadModel,
  UploadResponse,
  useFileUploadApi,
} from '@/api';

export const useUploadFile = () => {
  const { fileUploadApi } = useFileUploadApi();
  const queryClient = useQueryClient();

  const uploadFile = async (request: FileUploadModel): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', request.file);
    if (request.standardName) {
      formData.append('standardName', request.standardName);
    }
    if (request.version) {
      formData.append('version', request.version);
    }
    if (request.assessmentId) {
      formData.append('assessmentId', request.assessmentId);
    }
    if (request.standardId) {
      formData.append('standardId', request.standardId);
    }
    if (request.newAssessmentName) {
      formData.append('newAssessmentName', request.newAssessmentName);
    }

    const response = await fileUploadApi.fileUploadApiApiFileAssessmentUploadPost(
      request.file as unknown as string,
      request.standardName || null,
      request.version || null,
      request.assessmentId || null,
      request.standardId || null,
      request.newAssessmentName || null,
    );

    return response.data;
  };

  const mutation = useMutation({
    mutationFn: (request: FileUploadModel) => uploadFile(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
    onError: (error: Error) => {
      if (error.name === 'AbortError') {
        console.warn('Upload cancelled');
        return;
      }
      console.error('Upload failed:', error);
    },
  });

  return {
    uploadFile: mutation.mutateAsync,
    isUploading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useFileDownload = (filePath: string) => {
  const { fileUploadApi } = useFileUploadApi();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [FilesQueryKeys.minioFileDownload, filePath],
    queryFn: async () => {
      return await fileUploadApi.minioFileDownloadViewApiFileDownloadGet(filePath);
    },
    enabled: !!filePath,
  });

  return {
    downloadFileFromMinio: data,
    isDownloadFileLoading: isLoading,
    isDownloadFileError: isError,
    downloadFileRefetch: refetch,
  };
};

export const useDownloadScoreView = (assessmentId: string) => {
  const { fileUploadApi } = useFileUploadApi();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [FilesQueryKeys.downloadScoreView, assessmentId],
    queryFn: async () => {
      return await fileUploadApi.downloadAssessmentScoresViewApiFileAssessmentScoresDownloadAssessmentIdGet(assessmentId);
    },
    enabled: false,
    retry: false,
  });

  return {
    downloadScoreView: data,
    isDownloadScoreViewLoading: isLoading,
    isDownloadScoreViewError: isError,
    downloadScoreViewRefetch: refetch
  };
};